import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ArrowRight, Loader2, Rss } from "lucide-react";
import { MaskedLine, Overline, FadeUp } from "@/components/site/Reveal";
import { api } from "@/lib/api";

const fmtWhen = (iso) => {
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${date} • ${time}`;
  } catch {
    return "";
  }
};

const isNew = (iso) => Date.now() - new Date(iso).getTime() < 2 * 3600 * 1000;

const fmtLastUpdated = (ts) => {
  if (!ts) return null;
  try {
    return `${new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" })} ET`;
  } catch {
    return null;
  }
};

const WireCard = ({ item, categoryLabel }) => (
  <article
    className="group border-b border-white/10 py-5 transition-colors duration-300 first:border-t hover:bg-white/[0.02]"
    data-testid={`wire-item-${item.id}`}
  >
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.18em]">
      <span className="text-brand-accent" data-testid={`wire-source-${item.id}`}>{item.source}</span>
      <span className="text-white/35">{categoryLabel}</span>
      {item.is_breaking && (
        <span className="bg-brand-accent px-2 py-0.5 font-medium text-ink" data-testid={`wire-breaking-${item.id}`}>Breaking</span>
      )}
      {isNew(item.published_at) && !item.is_breaking && (
        <span className="border border-brand-accent/50 px-2 py-0.5 text-brand-accent">New</span>
      )}
      {item.featured && (
        <span className="border border-white/30 px-2 py-0.5 text-white/70">Featured</span>
      )}
    </div>
    <h3 className="mt-2 font-display text-lg font-extrabold uppercase leading-snug tracking-tight text-white transition-colors duration-300 group-hover:text-brand-accent sm:text-xl">
      {item.headline}
    </h3>
    <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">
        {fmtWhen(item.published_at)}
      </p>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`wire-read-${item.id}`}
        className="group/link inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-accent transition-colors duration-300 hover:text-white"
      >
        Read at Source
        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
      </a>
    </div>
  </article>
);

export default function News() {
  const [category, setCategory] = useState("latest");
  const [page, setPage] = useState(1);

  const { data: catData } = useQuery({
    queryKey: ["news-categories"],
    queryFn: () => api.get("/news/categories").then((r) => r.data.categories),
    staleTime: 300000,
  });
  const { data: wire, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ["news-items", category, page],
    queryFn: () => api.get("/news/items", { params: { category, page, limit: 20 } }).then((r) => r.data),
    refetchInterval: 60000,
    placeholderData: (prev) => prev,
  });
  const { data: originalsData } = useQuery({
    queryKey: ["news-originals"],
    queryFn: () => api.get("/news/originals").then((r) => r.data.originals),
    refetchInterval: 60000,
  });
  const { data: breakingData } = useQuery({
    queryKey: ["news-breaking"],
    queryFn: () => api.get("/news/breaking").then((r) => r.data.breaking),
    refetchInterval: 60000,
  });

  const categories = catData || [];
  const originals = originalsData || [];
  const breaking = breakingData || null;

  // Accumulate pages so Load More appends instead of replacing
  const [acc, setAcc] = useState([]);
  useEffect(() => {
    setAcc([]);
    setPage(1);
  }, [category]);
  useEffect(() => {
    if (!wire?.items) return;
    setAcc((prev) => {
      const base = page === 1 ? [] : prev;
      const ids = new Set(base.map((i) => i.id));
      return [...base, ...wire.items.filter((i) => !ids.has(i.id))];
    });
  }, [wire, page]);
  const items = acc;
  const totalPages = wire?.pages || 1;

  return (
    <main data-testid="news-page">
      <section className="mx-auto max-w-7xl px-5 pb-10 pt-36 md:px-8 md:pt-48" data-testid="news-hero">
        <MaskedLine>
          <Overline>Fore News</Overline>
        </MaskedLine>
        <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-7xl lg:text-8xl" data-testid="news-heading">
          <MaskedLine delay={0.15}>Golf</MaskedLine>
          <MaskedLine delay={0.3}><span className="text-brand-accent">News.</span></MaskedLine>
        </h1>
        <FadeUp delay={0.5}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-muted md:text-lg" data-testid="news-subtitle">
            The latest from around the game.
          </p>
        </FadeUp>

        {breaking && (
          <FadeUp delay={0.6}>
            <a
              href={breaking.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="news-breaking-banner"
              className="group mt-8 flex items-center gap-3 border-l-2 border-brand-accent bg-ink-2 px-4 py-3"
            >
              <span className="shrink-0 bg-brand-accent px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-ink">Breaking</span>
              <span className="truncate font-display text-sm font-bold uppercase tracking-tight text-white group-hover:text-brand-accent sm:text-base">
                {breaking.headline}
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-brand-accent" />
            </a>
          </FadeUp>
        )}
      </section>

      {/* FORE ORIGINALS — FORE-owned editorial, always above the wire */}
      <section className="bg-[#FBFAF7] text-neutral-900" data-testid="news-originals">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-col items-center text-center">
            <img src="/logo-editorial.png" alt="Fore the Hell of It" className="h-16 w-auto sm:h-20" data-testid="originals-masthead" />
            <h2 className="mt-6 font-mono text-[11px] font-bold uppercase tracking-[0.4em] text-neutral-900" data-testid="originals-heading">FORE Originals</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-500" data-testid="originals-tagline">
              Original FORE-created journalism, commentary, features and FORE Weekly.
            </p>
          </div>

          {originals.length === 0 ? (
            <div className="mt-12 border border-dashed border-neutral-300 py-14 text-center" data-testid="originals-empty">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                First FORE Original coming soon.
              </p>
            </div>
          ) : (
            <div className="mt-12">
              {(() => {
                const featured = originals.find((o) => o.featured) || originals[0];
                const rest = originals.filter((o) => o.id !== featured.id);
                return (
                  <>
                    <Link
                      to={`/news/${featured.slug}`}
                      data-testid={`original-featured-${featured.id}`}
                      className="group grid gap-8 border border-neutral-200 bg-white p-6 transition-colors duration-300 hover:border-neutral-900 sm:p-10 md:grid-cols-2 md:items-center"
                    >
                      {featured.hero_url && (
                        <img src={featured.hero_url} alt={featured.title} className="w-full" data-testid="original-featured-hero" />
                      )}
                      <div className={featured.hero_url ? "" : "md:col-span-2"}>
                        <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em]">
                          <span className="bg-neutral-900 px-2.5 py-1 font-bold text-white">FORE Original</span>
                          {featured.series && <span className="border border-[#E4572E] px-2.5 py-1 font-bold text-[#E4572E]">{featured.series}</span>}
                          <span className="text-neutral-500">Featured</span>
                        </div>
                        {featured.series_deck && (
                          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">{featured.series_deck}</p>
                        )}
                        <h3 className="mt-4 font-display text-3xl font-black leading-[1.02] tracking-tight text-neutral-900 transition-colors duration-300 group-hover:text-[#E4572E] sm:text-4xl lg:text-5xl">
                          {featured.title}
                        </h3>
                        {featured.subtitle && <p className="mt-4 text-base leading-relaxed text-neutral-600 md:text-lg">{featured.subtitle}</p>}
                        <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                          {featured.author} • {fmtWhen(featured.published_at)}
                        </p>
                        <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-neutral-900">
                          Read Article
                          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                      </div>
                    </Link>

                    {rest.length > 0 && (
                      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="originals-grid">
                        {rest.map((o) => (
                          <Link
                            key={o.id}
                            to={`/news/${o.slug}`}
                            data-testid={`original-${o.id}`}
                            className="group border border-neutral-200 bg-white p-6 transition-colors duration-300 hover:border-neutral-900"
                          >
                            {o.hero_url && <img src={o.hero_url} alt={o.title} className="mb-5 w-full" />}
                            <div className="flex flex-wrap items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em]">
                              <span className="bg-neutral-900 px-2 py-0.5 font-bold text-white">FORE Original</span>
                              {o.series && <span className="border border-[#E4572E] px-2 py-0.5 font-bold text-[#E4572E]">{o.series}</span>}
                            </div>
                            <h3 className="mt-4 font-display text-xl font-extrabold leading-tight tracking-tight text-neutral-900 transition-colors duration-300 group-hover:text-[#E4572E]">
                              {o.title}
                            </h3>
                            {o.subtitle && <p className="mt-3 text-sm leading-relaxed text-neutral-600">{o.subtitle}</p>}
                            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                              {o.author} • {fmtWhen(o.published_at)}
                            </p>
                            <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-neutral-900">
                              Read Article
                              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 md:px-8" data-testid="news-wire">
        <FadeUp className="border-t border-white/10 pt-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Rss className="h-4 w-4 text-brand-accent" />
              <Overline>FORE News Wire</Overline>
              <span className="hidden font-mono text-[10px] uppercase tracking-[0.15em] text-white/35 sm:inline" data-testid="wire-live">
                · Live{isFetching ? " — updating…" : ""}
              </span>
            </div>
            {dataUpdatedAt > 0 && (
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/40" data-testid="wire-last-updated">
                Last updated: {fmtLastUpdated(dataUpdatedAt)}
              </p>
            )}
          </div>
          <p className="mt-3 max-w-2xl font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-white/35" data-testid="wire-disclaimer">
            Live golf headlines from verified sources. FORE News Wire links directly to the original publisher.
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div data-lenis-prevent className="mt-6 flex gap-2 overflow-x-auto overscroll-x-contain pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="news-categories">
            {categories.map((c) => (
              <button
                key={c.slug}
                onClick={() => { setCategory(c.slug); setPage(1); }}
                data-testid={`news-cat-${c.slug}`}
                className={`shrink-0 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  category === c.slug
                    ? "border-brand-accent bg-brand-accent text-ink"
                    : "border-white/15 text-muted hover:border-brand-accent hover:text-brand-accent"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </FadeUp>

        <div className="mt-8 flex flex-col gap-4" data-testid="news-list">
          {items.length === 0 && !isFetching && (
            <div className="grid place-items-center border border-dashed border-white/15 py-20 text-center" data-testid="news-empty">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">
                Nothing in this category yet — the wire updates around the clock.
              </p>
            </div>
          )}
          {items.map((item) => (
            <WireCard key={item.id} item={item} categoryLabel={(categories.find((c) => c.slug === item.category) || {}).label || "Latest"} />
          ))}
        </div>

        {wire && page < wire.pages && (
          <div className="mt-10 text-center">
            <button
              onClick={() => setPage((p) => p + 1)}
              data-testid="news-load-more"
              className="border border-white/25 px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent"
            >
              Load More
            </button>
          </div>
        )}
        {isFetching && (
          <p className="mt-8 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/35">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…
          </p>
        )}
      </section>
    </main>
  );
}
